import React, { useMemo } from 'react';
import {
    AssetDto,
    Category,
    FindingDto,
    FindingKind,
    FindingStatus,
    KIND,
    SaasAssetDto,
    UserCoverageReport,
    UserDto,
} from '@ampsec/platform-client';
import { getAssetById, getFindings, getUserCoverageReport, getUserDetails } from 'Apis/library';
import { SourcePillsList } from '../InventoryGrid/cellRenderers/SourceCellRenderer';
import { getSaasAssetFromUserCoverageReport } from '../UserAssetCoverageGrid/buildUserAssetCoverageRows';
import { useParams } from 'react-router-dom';
import { Accordion } from 'Components/atoms';
import { AccordionItemProps } from 'Components/atoms/Accordion/Accordion';
import { EngagementDetailsAccordion } from '../EngagementDetailsAccordion';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { InfoCard } from 'Components/molecules/InfoCard';
import { EntityLink } from 'Components/molecules/EntityLink';
import ErrorHandlerAndLoaderWrapper from 'Wrappers/ErrorHandlerandLoaderWrapper';
import EngageIcon from 'Assets/icons/engagement.svg';
import { FINDINGS_ROUTE, USERS_DASHBOARD_ROUTE } from 'Core-utils/routes';
import { useEarlyAccess } from 'Hooks/useEarlyAccess';
import _ from 'lodash';
import { DateTimeFormatter } from 'Core-utils/helpers/helpers';
import { GoTag } from '@react-icons/all-files/go/GoTag';

type DataMapEntry = { key: string; value: string };

function getConnectorAssetDetailsTitle(findingData: FindingDto | null | undefined): string {
  if (!findingData || !findingData.provider) {
    return 'CONNECTOR ASSET DETAILS';
  }
  return `${findingData.provider?.displayValue} ASSET DETAILS`;
}

function getFindingDiscoveredAtLabel(findingData: FindingDto | null | undefined): string {
  if (!findingData) {
    return '';
  }
  if (findingData.category === Category.COMPLIANCE_TRAINING) {
    return 'Assigned At';
  }
  return 'Discovered At';
}

function getFindingClosedAt(findingData: FindingDto | null | undefined): string {
  if (!findingData) {
      return '-';
  }
  if (findingData.status !== FindingStatus.CLOSED && findingData.status !== FindingStatus.CLOSED_BY_OMISSION) {
    return '-';
  }
  if (findingData.status === FindingStatus.CLOSED_BY_OMISSION) {
    return DateTimeFormatter(findingData?.updatedAt ?? '') ?? '-';
  }

  if (findingData.closedAt) {
    return DateTimeFormatter(findingData?.closedAt ?? '') ?? '-';
  }

  return DateTimeFormatter(findingData?.updatedAt ?? '') ?? '-';
}

function getFindingClosedAtLabel(findingData: FindingDto | null | undefined): string {
  if (!findingData) {
    return '';
  }
  if (findingData.category === Category.COMPLIANCE_TRAINING) {
    return 'Completed At';
  }
  return 'Closed At';
}

function getRawFindingsDataMap(findingData: FindingDto | null | undefined): DataMapEntry[] {
    if (!findingData) {
        return [];
    }

    const { category, kind, meta } = findingData;

    switch (category) {
        case Category.AI_SERVICE_RISK: {
            const aiServices = meta?._aiservices;
            if (!aiServices) {
                return [];
            }

            const isUnapproved = kind === FindingKind.AI_SERVICE_UNAPPROVED;
            const services = isUnapproved ? (aiServices.unapproved ?? []) : (aiServices.untracked ?? []);
            const serviceLabel = isUnapproved ? 'Unapproved Services' : 'Untracked Services';

            return [
                { key: 'Service Count', value: services.length.toString() },
                { key: serviceLabel, value: services.length > 0 ? services.join(', ') : 'None' },
            ];
        }
        case Category.VULNERABILITY:
            return [
                { key: 'Vulnerability CVE', value: meta?._vulnerability?.cve ?? '-' },
                { key: 'Vulnerability CVSS', value: meta?._vulnerability?.cvss?.toString() ?? '-' },
            ];
        case Category.TRAINING:
        case Category.COMPLIANCE_TRAINING:
            return [
                { key: 'Training Title', value: meta?._training?.title ?? '-' },
                { key: 'Enroll Date', value: DateTimeFormatter(meta?._training?.enrollDate ?? '') ?? '-' },
                { key: 'Due Date', value: DateTimeFormatter(meta?._training?.dueDate ?? '') ?? '-' },
            ];
        case Category.CUSTOM:
            return [{ key: 'Event Type', value: meta?._custom?.eventType ?? '-' }];
        case Category.COMPLIANCE:
            return [
                { key: 'Benchmark', value: meta?._compliance?.benchmark?.title ?? '-' },
                { key: 'Rule', value: meta?._compliance?.rule?.title ?? '-' },
            ];
        default:
            return [];
    }
}

function getSaasAssetDataMap(saasAssetData: SaasAssetDto | null | undefined): DataMapEntry[] {
    if (!saasAssetData) {
        return [];
    }

    const assetMeta = saasAssetData.meta?._asset;
    const hostNames = assetMeta?.hostNames?.filter(Boolean) ?? [];
    const operatingSystem = assetMeta?.operatingSystems?.[0];
    const macs = saasAssetData.macs?.length ? saasAssetData.macs : assetMeta?.macs;
    const attribution = assetMeta?.attribution;

    return [
        { key: 'Display Name', value: saasAssetData.displayValue ?? '-' },
        { key: 'Serial Number', value: saasAssetData.sn ?? assetMeta?.sn ?? '-' },
        { key: 'Host Name', value: hostNames.length > 0 ? hostNames.join(', ') : '-' },
        { key: 'Operating System', value: operatingSystem?.displayValue || '-' },
        { key: 'OS Version', value: operatingSystem?.version || '-' },
        { key: 'Make', value: assetMeta?.make ?? '-' },
        { key: 'Model', value: assetMeta?.model ?? '-' },
        { key: 'Active Status', value: assetMeta?.activeStatus ?? '-' },
        { key: 'Owner Email', value: assetMeta?.owner?.email ?? '-' },
        { key: 'MAC Addresses', value: macs?.length ? macs.join(', ') : '-' },
        { key: 'Last Activity', value: DateTimeFormatter(assetMeta?.lastActivityTime ?? '') || '-' },
        ...(attribution
            ? [
                  { key: 'Attribution Status', value: attribution.status ?? '-' },
                  { key: 'Attribution Reason', value: attribution.reason ?? '-' },
              ]
            : []),
    ];
}

const FindingDetailContentWrapper = () => {
    const { id: findingId } = useParams();
    const { data: findingData, error, isLoading, refresh } = useApiObjectData<FindingDto, FindingDto>(
        KIND.FINDINGS,
        () => getFindings({ id: findingId, include: ['meta', 'asset', 'provider', 'user', 'tags'] }),
        (a) => a,
    );

    const {
        data: userData,
        error: userDataError,
        isLoading: userDataIsLoading,
        refresh: userDataRefresh,
    } = useApiObjectData<UserDto, UserDto>(
        findingData?.uid ?? '',
        findingData?.uid ? getUserDetails : () => Promise.resolve(null),
    );

    const {
        data: assetData,
        error: assetDataError,
        isLoading: assetDataIsLoading,
        refresh: assetDataRefresh,
    } = useApiObjectData<AssetDto, AssetDto>(
        findingData?.aid ?? '',
        findingData?.aid ? getAssetById : () => Promise.resolve(null),
    );

    const findingUserId = findingData?.uid ?? findingData?.user?.id ?? '';
    const shouldFetchUserCoverage = !!findingUserId && !!findingData?.aid && !!findingData?.cid;
    const {
        data: userCoverageReport,
        error: userCoverageReportError,
        isLoading: userCoverageReportIsLoading,
        refresh: userCoverageReportRefresh,
    } = useApiObjectData<UserCoverageReport, UserCoverageReport>(
        `finding-user-coverage-${findingUserId}`,
        () => getUserCoverageReport(findingUserId),
        (report) => report,
        undefined,
        shouldFetchUserCoverage,
    );
    const saasAssetData = useMemo(
        () =>
            getSaasAssetFromUserCoverageReport(
                userCoverageReport,
                findingData?.aid ?? '',
                findingData?.cid ?? '',
            ),
        [userCoverageReport, findingData?.aid, findingData?.cid],
    );

    const { isLoading: earlyAccessLoading, isDevTier, isBetaTier } = useEarlyAccess();
    const canLinkToFindings = !earlyAccessLoading && (isDevTier || isBetaTier);

    const findingsFilterLink = (urlParam: string, value: string | undefined): React.ReactNode => {
        if (!canLinkToFindings || !value) {
            return value || '-';
        }
        return (
            <EntityLink
                to={`${FINDINGS_ROUTE}?${urlParam}=${encodeURIComponent(value)}&sort=status`}
                label={value}
                entityType="finding-filter"
                withContextHint={false}
            />
        );
    };

    const findingsDataMap: { key: string; value: React.ReactNode }[] = [
        {
            key: 'Description',
            value: findingData?.description ?? '-',
        },
        {
            key: 'Severity',
            value: findingData?.severity ?? '-',
        },
        {
            key: 'Status',
            value: findingData?.status ?? '-',
        },
        {
            key: 'Issue Type',
            value: findingsFilterLink('finding_category', findingData?.category),
        },
        {
            key: 'Provider',
            value: findingsFilterLink('provider_name', findingData?.provider?.displayValue),
        },
        {
            key: 'User',
            value: (() => {
                const fullName = `${findingData?.user?.firstName ?? ''} ${findingData?.user?.lastName ?? ''}`.trim();
                return findingData?.uid && fullName ? (
                    <EntityLink
                        to={`${USERS_DASHBOARD_ROUTE}/${findingData.uid}`}
                        label={fullName}
                        entityType="user"
                        withContextHint={false}
                    />
                ) : (
                    fullName || '-'
                );
            })(),
        },
        {
            key: 'Department',
            value: findingsFilterLink('user_department', findingData?.user?.department),
        },
        {
            key: 'Device',
            value: findingData?.asset?.displayValue ?? '-',
        },
        {
            key: getFindingDiscoveredAtLabel(findingData),
            value: DateTimeFormatter(findingData?.discoveredAt ?? ''),
        },
        {
          key: getFindingClosedAtLabel(findingData),
          value: getFindingClosedAt(findingData),
        },
        {
            key: 'Tags',
            value: isLoading ? (
                '—'
            ) : (
                <SourcePillsList value={findingData?.finding_tags ?? []} maxPills={10} icon={<GoTag />} />
            ),
        },
    ];
    const userDataMap: { key: string; value: React.ReactNode }[] = [
        {
            key: 'Name',
            value: (() => {
              const fullName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
              return userData?.id && fullName ? (
                  <EntityLink
                      to={`${USERS_DASHBOARD_ROUTE}/${userData.id}`}
                      label={fullName}
                      entityType="user"
                      withContextHint={false}
                  />
              ) : (
                  fullName || '-'
              );
          })(),
            //value: `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim().toString() ?? '-',
        },
        {
            key: 'Email',
            value: userData?.emails?.[0] ?? '-',
        },
        {
            key: 'Department',
            value: findingsFilterLink('user_department', findingData?.user?.department),
        },
        {
            key: 'Score',
            value: userData?.score?.toString() ?? '-',
        },
        {
            key: 'Last Activity',
            value: DateTimeFormatter(userData?.lastActivityTime ?? ''),
        },
    ];
    const assetDataMap: { key: string; value: string }[] = [
        {
            key: 'Display Name',
            value: assetData?.displayValue ?? '-',
        },
        {
            key: 'Serial Number',
            value: assetData?.extKeys?.sn ?? '-',
        },
        {
            key: 'Host Name',
            value: assetData?.extKeys?.hostname ?? '-',
        },
        {
            key: 'Last CheckIn',
            value: DateTimeFormatter(assetData?.lastActivityTime ?? ''),
        },
    ];
    const saasAssetDataMap = getSaasAssetDataMap(saasAssetData);
    const showConnectorAssetSection = shouldFetchUserCoverage;
    const rawFindingsDataMap = getRawFindingsDataMap(findingData);
    const showRawFindingsSection = isLoading || !!error || rawFindingsDataMap.length > 0;
    const accordionItems: AccordionItemProps[] = [
        {
            key: '0',
            eventKey: '0',
            title: 'FINDINGS DETAILS',
            isConfigurationRequired: false,
            children: (
                <ErrorHandlerAndLoaderWrapper
                    error={error}
                    isLoading={isLoading}
                    handleRefresh={refresh}
                    ImageSrc={EngageIcon}
                    render={() => <InfoCard dataMap={findingsDataMap} data-testid="findings-data-map" />}
                    isEmpty={_.isEmpty(findingData)}
                />
            ),
        },
        {
            key: '1',
            eventKey: '1',
            title: 'ENGAGEMENTS DETAILS',
            isConfigurationRequired: false,
            children: <EngagementDetailsAccordion findingData={findingData as FindingDto} />,
        },
        {
            key: '2',
            eventKey: '2',
            title: 'USER DETAILS',
            isConfigurationRequired: false,
            children: (
                <ErrorHandlerAndLoaderWrapper
                    error={userDataError}
                    isLoading={userDataIsLoading}
                    handleRefresh={userDataRefresh}
                    ImageSrc={EngageIcon}
                    render={() => <InfoCard dataMap={userDataMap} />}
                    isEmpty={_.isEmpty(userData)}
                />
            ),
        },
        {
            key: '3',
            eventKey: '3',
            title: 'ASSET DETAILS',
            isConfigurationRequired: false,
            children: (
                <ErrorHandlerAndLoaderWrapper
                    error={assetDataError}
                    isLoading={assetDataIsLoading}
                    handleRefresh={assetDataRefresh}
                    ImageSrc={EngageIcon}
                    render={() => <InfoCard dataMap={assetDataMap} />}
                    isEmpty={_.isEmpty(assetData) || findingData?.aid === undefined || findingData?.aid === null}
                    description="No Asset Found"
                />
            ),
        },
        ...(showConnectorAssetSection
            ? [
                  {
                      key: '4',
                      eventKey: '4',
                      title: getConnectorAssetDetailsTitle(findingData),
                      isConfigurationRequired: false as const,
                      children: (
                          <ErrorHandlerAndLoaderWrapper
                              error={userCoverageReportError}
                              isLoading={userCoverageReportIsLoading}
                              handleRefresh={userCoverageReportRefresh}
                              ImageSrc={EngageIcon}
                              render={() => <InfoCard dataMap={saasAssetDataMap} data-testid="connector-asset-data-map" />}
                              isEmpty={_.isEmpty(saasAssetData)}
                              description="Connector asset details are not available"
                          />
                      ),
                  },
              ]
            : []),
        ...(showRawFindingsSection
            ? [
                  {
                      key: '5',
                      eventKey: '5',
                      title: 'RAW FINDINGS DETAILS',
                      isConfigurationRequired: false as const,
                      children: (
                          <ErrorHandlerAndLoaderWrapper
                              error={error}
                              isLoading={isLoading}
                              handleRefresh={refresh}
                              ImageSrc={EngageIcon}
                              render={() => <InfoCard dataMap={rawFindingsDataMap} />}
                              isEmpty={_.isEmpty(findingData)}
                          />
                      ),
                  },
              ]
            : []),
    ];

    return (
        <Accordion
            accordionItems={accordionItems}
            defaultActiveKey={['0', '1', '2', '3', '5']}
            alwaysOpen={true}
            data-testid="finding-detail-content"
        />
    );
};

export default FindingDetailContentWrapper;
